import { Component, inject, ViewChild } from '@angular/core';
import { NewSearchViewModel } from '../models/new-search-view.model';
import { Product } from '@shared/models/product.model';
import { searchLoadingState } from '@usf/ngrx-product';
import { SelectionType } from '../models/selection-type.enum';
import { PATHS } from '@shared/constants/paths';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import {
  SEARCH_RECORD_PER_BATCH,
  SMALL_RESULT_COUNT_SPACER_HEIGHT,
} from '@search/shared/constants/search-config';
import {
  DELIVERY_METHOD_FACET_FIELD_NAME,
  DELIVERY_METHOD_US_FOODS,
  FacetChipViewModel,
} from '@app/new-search/models/facet-filter.model';
import {
  calculateDesktopProductHeightForSearch,
  calculateMobileProductHeightForSearch,
  calculateTabletProductHeightForSearch,
} from '@search/utils/search-card.util';
import { NewSearchResultComponent } from '../components/new-search-result/new-search-result.component';
import { InventoryService } from '@inventory/services/inventory.service';
import { SearchDeliveryToggleService } from '@search/services/search-delivery-toggle.service';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import {
  CATEGORY_FACET,
  DELIVERY_TYPE_FACET,
  FACETS_TO_EXCLUDE_FROM_ACTION_BAR,
  FACETS_TYPE_TO_INCLUDE,
  NEW_SEARCH_ERROR_MESSAGES,
  OUR_EXCLUSIVES_FACET,
  US_FOODS_DELIVERED_VALUE,
  SEARCH_TYPE,
} from '../constants/new-search-constants';
import { PowerReviewRollupModel } from '@usf/ngrx-product/lib/models/power-review-product.model';
import { NewSearchService } from '../services/new-search.service';
import {
  delay,
  filter,
  lastValueFrom,
  map,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { PowerReviewService } from '@shared/services/power-review.service';
import { ListsPageData } from '@app/lists/model/lists-page.model';
import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { SearchResultViewModel } from '@search/shared/models/search-view.model';
import { ListsService } from '@app/lists/services/lists.service';
import { buildFacetFilterObject } from '../utils/new-search-filters.util';
import { CoveoService } from '@shared/services/coveo/coveo.service';
import { ProductService } from '@shared/services/product/product.service';
import {
  buildSearch,
  CommerceEngine,
  ConfigurationActionCreators,
  DateFacet,
  FacetGenerator,
  loadConfigurationActions,
  loadSearchActions,
  QueryActionCreators,
  RegularFacet,
  CategoryFacet,
  NumericFacet,
  Search,
  SearchActionCreators,
  Unsubscribe,
  CategoryFacetSetActionCreators,
  BreadcrumbManager,
  Summary,
  CategoryFacetValue,
  LocationFacet,
  RegularFacetValue,
  GeneratedFacetControllers,
  Pagination,
  CommerceSearchParameters,
  ParameterManager,
  RedirectionTrigger,
  buildRedirectionTrigger,
  DidYouMean,
  SearchBox,
  buildSearchBox,
  SearchBoxState,
  ProductListing,
  buildProductListing,
  Recommendations,
  buildRecommendations,
  NotifyTrigger,
  buildNotifyTrigger,
} from '@coveo/headless/commerce';
import {
  FacetViewEvent,
  FacetViewOperation,
  ListFacetFilterKey,
  ListFacetSharedObject,
  ListFacetValue,
  ToggleFacetEvent,
} from '../models/new-search-facets.model';
import { UserService } from '@app/user/services';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { Location } from '@angular/common';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { environment } from 'src/environments/environment';
import { IonContent } from '@ionic/angular';
import { RecipeDetailService } from '@app/mpp/pages/recipe-detail/services/recipe-detail.service';
import { SelectedCustomerState } from '@panamax/app-state/lib/models/customer.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
export class NewSearchBasePage {
  @ViewChild(NewSearchResultComponent)
  newSearchResultComponent: NewSearchResultComponent;

  @ViewChild(IonContent)
  ionContent: IonContent;

  searchDeliveryToggleService = inject(SearchDeliveryToggleService);
  navigationHelperService = inject(NavigationHelperService);
  inventoryService = inject(InventoryService);
  platformService = inject(PlatformService);
  powerReviewService = inject(PowerReviewService);
  newSearchService = inject(NewSearchService);
  panAppState = inject(PanAppState);
  coveoService = inject(CoveoService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  translateService = inject(TranslateService);

  selection: SelectionType = null;
  searchResult$: Subscription;
  listPageDataSub$: Subscription;
  clearSearchSub$: Subscription;
  toggleStateSub$: Subscription;
  isTouchExperience$: Subscription;
  coveoClickEventSub$: Subscription;
  distinctSelectedCustomer$: Subscription;

  newSearchViewModel: NewSearchViewModel;
  selectedProducts: Product[];
  itemHeights: number[];

  pwrRatingStarsMap: Observable<Map<string, PowerReviewRollupModel>>;
  powerReviewsMap: Map<string, PowerReviewRollupModel>;
  isLastPage: boolean;
  listPageData: ListsPageData;
  isAvendraUser: boolean;
  isGuestUser: boolean;
  showInventory: boolean;
  firstLoadPerformed: boolean;
  isLoadingNextBatch: boolean;
  isTouchExperience: boolean;
  isLoadingFacetValues = false;
  readonly facetViewEnum = FacetViewOperation;
  selectedProductText: string;
  isAddToList = false;
  isInventory = false;
  isAddIngredient = false;
  originSearchPage: string;
  backButtonText: string;
  pageType: string;
  searchQueryParamStack = [];
  listFacetSharedObject: ListFacetSharedObject;

  replacementBaseProduct: SearchResultViewModel;

  coveoCommerceEngine: CommerceEngine;
  searchEngineSubscription$: Subscription;
  commerceEngineSubscription$: Subscription;
  coveoSearch: Search | ProductListing;
  queryActions: QueryActionCreators;
  configurationActions: ConfigurationActionCreators;
  searchActions: SearchActionCreators;
  categoryFacetActions: CategoryFacetSetActionCreators;
  facetGenerator: FacetGenerator;
  breadcrumbManager: BreadcrumbManager;
  summary: Summary;
  searchControllerUnsubcribe: Unsubscribe;
  deliveryFacet: RegularFacet = null;
  usfDeliveredFacetValue: RegularFacetValue = null;
  ourExclusivesFacet: RegularFacet = null;
  selectedCustomerState: SelectedCustomerState;

  commerceEngineFacets: Map<
    string,
    RegularFacet | DateFacet | NumericFacet | CategoryFacet | LocationFacet
  >;
  facetChipViewModels: FacetChipViewModel[] = [];
  actionBarFacetChipViewModels: FacetChipViewModel[] = [];
  actionBarFacets: GeneratedFacetControllers;
  flyoutFacets: GeneratedFacetControllers = [];
  hasFacetsApplied = false;
  currentSearchTerm = '';

  productsSubscription$: Subscription;
  isGuestUser$: Subscription;
  showInventory$: Subscription;

  topSellerProducts$: Observable<MerchandisingProductViewModel[]>;
  redirectionTrigger: RedirectionTrigger;

  paginationController: Pagination;
  parameterManagerController: ParameterManager<CommerceSearchParameters>;
  parameterManagerUnsubscribe: Unsubscribe;

  // did you mean
  didYouMeanController: DidYouMean;
  didYouMeancontrollerUnsubscribe: Unsubscribe;
  didYouMean: boolean;
  originalQuery: string;
  wasCorrectedTo: string;

  // search box controller
  searchBoxController: SearchBox;
  searchBoxState: SearchBoxState;
  searchBoxControllerUnscubscribe: Unsubscribe;

  // MODAL params:
  isInPageSearchModalOpen = false;

  defaultSelectionType: SelectionType;

  recommendationsController: Recommendations;
  recommendationsControllerUnsubscribe: Unsubscribe;
  newTopSellersProductNumbers: number[] = [];
  newTopSellers$: Observable<ProductDetailViewModel[]>;

  notificationController: NotifyTrigger;
  notificationControllerUnsubscribe: Unsubscribe;

  // TOP SECTION

  isTopSection: boolean;
  topSectionBackgroundImageStyle: string;
  topSectionBackgroundPosition: any;
  topSectionImageTitle: any;
  topSectionTextTitle: any;
  topSectionTextContent: any;
  isTopSectionTextExpanded: boolean;

  constructor(
    readonly listsOverlayService: ListsOverlayService,
    readonly addToListModalService: AddToListModalService,
    readonly listsService: ListsService,
    readonly productService: ProductService,
    readonly location: Location,
    readonly recipeDetailService: RecipeDetailService,
    readonly userService?: UserService,
  ) {
    this.isGuestUser$ = this.userService
      .isGuestUser$()
      .subscribe(value => (this.isGuestUser = value));
  }

  initializeCoveo(options?: {
    initializeSearchBoxController?: boolean;
    initializeNotificationController?: boolean;
    pageType?: string;
  }) {
    this.commerceEngineSubscription$ = this.coveoService
      .getCommerceEngine$()
      .pipe(filter(engine => !!engine))
      .subscribe(engine => {
        this.pageType = options?.pageType;
        this.coveoCommerceEngine = engine;

        if (options?.pageType === 'productListing') {
          this.coveoSearch = buildProductListing(this.coveoCommerceEngine);
        } else {
          this.coveoSearch = buildSearch(this.coveoCommerceEngine);
        }
        this.paginationController = this.coveoSearch.pagination({
          options: { pageSize: 50 },
        });
        this.facetGenerator = this.coveoSearch.facetGenerator();

        this.configurationActions = loadConfigurationActions(
          this.coveoCommerceEngine,
        );
        this.searchActions = loadSearchActions(this.coveoCommerceEngine);
        this.recommendationsController = buildRecommendations(
          this.coveoCommerceEngine,
          {
            options: {
              slotId: `${environment.coveoRecommendationsSlots.topSellersNoSearchResults}`,
            },
          },
        );

        this.recommendationsControllerUnsubscribe =
          this.recommendationsController.subscribe(() => {
            this.newTopSellersProductNumbers =
              this.recommendationsController?.state['products']?.map(product =>
                Number(product.ec_product_id),
              );
            this.newTopSellers$ = this.fetchTopSellers$(
              this.newTopSellersProductNumbers,
            ).pipe(map(products => products.slice(0, 12)));
          });

        this.newSearchViewModel = {
          products: [],
          loadingState: searchLoadingState.loading,
          facetLoadingState: searchLoadingState.loading,
        };

        this.searchControllerUnsubcribe = this.coveoSearch.subscribe(() => {
          const searchQueryParam = this.coveoSearch.state['queryExecuted'];
          if (
            this.isNewSearchExecuted(searchQueryParam) &&
            !this.isListFacetWithParamExist()
          ) {
            this.clearListFacetFilter();
          }

          if (
            this.coveoSearch?.state?.isLoading === false &&
            !this.coveoSearch?.state?.error
          ) {
            this.handleSearchLoaded();
          }
          if (
            this.coveoSearch?.state?.isLoading &&
            this.isLoadingFacetValues === false
          ) {
            this.newSearchViewModel.facetLoadingState =
              this.SearchLoadingState.loading;
            this.newSearchViewModel.loadingState =
              this.SearchLoadingState.loading;
          }
          if (this.coveoSearch?.state?.error) {
            this.newSearchViewModel.facetLoadingState =
              this.SearchLoadingState.error;
            this.newSearchViewModel.loadingState =
              this.SearchLoadingState.error;
            this.newSearchViewModel.totalCount = 0;
            this.newSearchViewModel.searchText = this.currentSearchTerm;
          }
          if (this.coveoSearch?.state.isLoading === false) {
            this.isLoadingFacetValues = false;
          }
        });

        this.summary = this.coveoSearch.summary();

        this.redirectionTrigger = buildRedirectionTrigger(
          this.coveoCommerceEngine,
        );

        let params: Params = this.navigationHelperService.getParamAsObject();
        this.setUpListFacetWithParam(params);
        this.setUpParameterManager(params);
        if (options?.pageType === 'search') {
          this.setUpDidYouMean();
        }
        options?.initializeSearchBoxController &&
          this.setUpSearchBoxController(engine);
        if (options?.pageType === 'productListing') {
          let listingController = this.coveoSearch as ProductListing;
          listingController.executeFirstRequest();
        } else {
          let searchController = this.coveoSearch as Search;
          searchController.executeFirstSearch();
        }
        if (options?.initializeNotificationController) {
          this.notificationController = buildNotifyTrigger(
            this.coveoCommerceEngine,
          );
        }
      });
  }
  setUpSearchBoxController(engine: CommerceEngine) {
    this.searchBoxController = buildSearchBox(engine);
    this.searchBoxControllerUnscubscribe = this.searchBoxController.subscribe(
      () => {
        this.searchBoxState = this.searchBoxController.state;
      },
    );
  }

  handleSearchLoaded() {
    if (
      this.redirectionTrigger &&
      this.currentSearchTerm === this.coveoSearch.state['queryExecuted']
    ) {
      const redirectTo = this.redirectionTrigger.state?.redirectTo;
      if (redirectTo) {
        const path = new URL(redirectTo).pathname;
        this.router.navigate([path]);
      }
    }

    this.setUpFacets();

    this.newSearchViewModel = {
      products: [],
      totalCount: this.summary?.state?.totalNumberOfProducts ?? 0,
      facetLoadingState: this.SearchLoadingState.loaded,
      loadingState: this.SearchLoadingState.loading,
      searchText:
        this.coveoSearch.state['queryExecuted']?.length > 0
          ? this.coveoSearch.state['queryExecuted']
          : null,
    };

    this.getSearchResult();
  }

  setUpFacets() {
    this.facetChipViewModels = [];
    this.actionBarFacetChipViewModels = [];
    this.usfDeliveredFacetValue = null;
    this.ourExclusivesFacet = null;
    this.deliveryFacet = null;
    this.ourExclusivesFacet = null;

    this.facetGenerator.facets.forEach(facet => {
      if (
        facet.type === 'regular' &&
        facet.state.facetId === DELIVERY_TYPE_FACET
      ) {
        this.deliveryFacet = facet;

        this.usfDeliveredFacetValue = this.deliveryFacet?.state.values.find(
          facetValue =>
            facetValue.value === US_FOODS_DELIVERED_VALUE &&
            (facetValue.numberOfResults <
              this.summary?.state?.totalNumberOfProducts ||
              facetValue.state === 'selected'),
        );
      }

      if (
        facet.type === 'regular' &&
        facet.state.facetId === OUR_EXCLUSIVES_FACET
      ) {
        this.ourExclusivesFacet = facet;
      }

      if (facet.state.hasActiveValues) {
        if (facet.type === 'regular') {
          facet.state.values.forEach(value => {
            if (value.state === 'selected') {
              this.facetChipViewModels.push({
                facet: facet,
                facetValue: value,
              });

              if (value.value !== US_FOODS_DELIVERED_VALUE) {
                this.actionBarFacetChipViewModels.push({
                  facet: facet,
                  facetValue: value,
                });
              }
            }
          });
        }

        if (facet.type === 'hierarchical') {
          this.facetChipViewModels.push({
            facet: facet,
            facetValue:
              facet.state?.selectedValueAncestry[
                facet.state.selectedValueAncestry?.length - 1
              ],
          });
        }
      }
    });

    this.actionBarFacets = this.facetGenerator.facets.filter(
      facet =>
        !FACETS_TO_EXCLUDE_FROM_ACTION_BAR.includes(facet.state.facetId) &&
        FACETS_TYPE_TO_INCLUDE.includes(facet?.type),
    );
    this.flyoutFacets = [
      this.deliveryFacet,
      this.ourExclusivesFacet,
      ...this.actionBarFacets,
    ].filter(
      v =>
        v !== null && !!v?.state?.displayName && v?.state?.displayName !== '',
    );

    this.hasFacetsApplied =
      this.actionBarFacetChipViewModels.length > 0 ||
      this.listFacetSharedObject?.isValuesSelected;
  }

  coveoTeardown() {
    this.productsSubscription$?.unsubscribe();
    if (!!this.searchControllerUnsubcribe) {
      this.searchControllerUnsubcribe();
    }
    if (this.parameterManagerUnsubscribe) {
      this.parameterManagerUnsubscribe();
    }
    if (this.didYouMeancontrollerUnsubscribe) {
      this.didYouMeancontrollerUnsubscribe();
    }
    if (this.searchBoxControllerUnscubscribe) {
      this.searchBoxControllerUnscubscribe();
    }
    if (this.recommendationsControllerUnsubscribe) {
      this.recommendationsControllerUnsubscribe();
    }
    if (this.notificationControllerUnsubscribe) {
      this.notificationControllerUnsubscribe();
    }
    this.commerceEngineSubscription$?.unsubscribe();
  }
  setUpDidYouMean() {
    let searchController = this.coveoSearch as Search;
    if (searchController) {
      this.didYouMeanController = searchController.didYouMean();
      this.didYouMeancontrollerUnsubscribe =
        this.didYouMeanController.subscribe(() => {
          this.didYouMean = this.didYouMeanController.state.hasQueryCorrection;
          this.originalQuery = this.didYouMeanController.state.originalQuery;
          this.wasCorrectedTo = this.didYouMeanController.state.wasCorrectedTo;
        });
    }
  }

  setUpParameterManager(params: Params) {
    this.parameterManagerController = this.coveoSearch.parameterManager({
      initialState: {
        parameters: this.convertParamsToCoveo(params),
      },
    });

    this.parameterManagerUnsubscribe =
      this.parameterManagerController.subscribe(() => {
        const currentUrl = this.router.url;
        const isSearchPage = currentUrl.includes(PATHS.NEW_SEARCH);
        if (isSearchPage) {
          let queryParams = this.convertSearchStateToQueryParams(
            this.parameterManagerController.state.parameters,
          );

          const paramObj = this.navigationHelperService.getParamAsObject();
          delete paramObj['facetFilters'];
          delete paramObj['searchText'];
          queryParams = { ...paramObj, ...queryParams };

          this.scrollToTop();

          const url = this.router
            .createUrlTree([], {
              relativeTo: this.route,
              queryParams,
            })
            .toString();
          this.location.replaceState(url);
        }
      });
  }

  initializeSelectedCustomer() {
    this.distinctSelectedCustomer$ = this.panAppState
      .getDistinctSelectedCustomer()
      .pipe(filter(customer => customer?.selected))
      .subscribe(selectedCustomer => {
        this.selectedCustomerState = selectedCustomer;
      });
  }

  convertParamsToCoveo(params: Params): CommerceSearchParameters {
    let coveoParams = {} as CommerceSearchParameters;

    if (params.searchText) {
      coveoParams.q = params.searchText;
    }

    if (params.facetFilters) {
      let facetFilters = params.facetFilters.split(',');
      for (const filter of facetFilters) {
        let [facet, facetValuesString] = filter.split(':');

        if (!!facet && !!facetValuesString) {
          const facetValues = facetValuesString.split('+');

          if (facet === CATEGORY_FACET) {
            coveoParams.cf = {};
            coveoParams.cf[facet] = facetValues.map(val =>
              decodeURIComponent(val),
            );
          } else {
            coveoParams.f = coveoParams.f || {};
            coveoParams.f[facet] = facetValues.map(val =>
              decodeURIComponent(val),
            );
          }
        }
      }
    }

    return coveoParams;
  }

  convertSearchStateToQueryParams(
    coveoParams: CommerceSearchParameters,
  ): Params {
    let queryParams = {} as Params;
    if (coveoParams.q) {
      queryParams.searchText = coveoParams.q;
    }
    if (!!coveoParams.f) {
      const facets = coveoParams.f;
      let facetString = '';
      for (const [facet, facetValues] of Object.entries(facets)) {
        facetString += `${facet}:${facetValues.map(val => encodeURIComponent(val)).join('+')},`;
      }
      facetString = facetString.slice(0, -1);
      queryParams.facetFilters = facetString;
    }

    if (!!coveoParams.cf) {
      let facetString = !!queryParams.facetFilters
        ? queryParams.facetFilters + ','
        : '';
      for (const [facet, facetValues] of Object.entries(coveoParams.cf)) {
        facetString += `${facet}:${facetValues.map(val => encodeURIComponent(val)).join('+')},`;
      }
      facetString = facetString.slice(0, -1);
      queryParams.facetFilters = facetString;
    }
    return queryParams;
  }

  handleSelectionTypeChange(selectionType: SelectionType) {
    this.selection = this.selection === selectionType ? null : selectionType;

    if (!this.selection) {
      this.selection = this.defaultSelectionType;
    }
    this.clearSelectedProducts();
    this.addSelectionTypeToUrl();
    // calling it with selectionType instead of this.selection to manage the unselect case
    // this.selection will be set to the defaultSelectionType in the unselect case
    this.updateBaseProduct(selectionType);
  }

  updateBaseProduct(selectionType: SelectionType) {
    if (
      selectionType === SelectionType.CompareProducts &&
      this.replacementBaseProduct
    ) {
      this.replacementBaseProduct.selected =
        !this.replacementBaseProduct.selected;
      this.replacementBaseProduct.disabled =
        !this.replacementBaseProduct.disabled;
      if (this.replacementBaseProduct.selected) {
        this.selectedProducts = [this.replacementBaseProduct];
      }
    }
  }

  addSelectionTypeToUrl() {
    let paramObj = {};
    paramObj = this.navigationHelperService.getParamAsObject();
    const queryParams = {
      ...paramObj,
      selectionType: this.selection,
    };

    const url = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      })
      .toString();

    // using this.location.replaceState to avoid reloading the page
    this.location.replaceState(url);
  }

  clearSelectedProducts() {
    this.newSearchViewModel?.products?.forEach(product => {
      product.selected = false;
      product.disabled = false;
    });
    this.selectedProducts = [];
    // the clearSelectedProducts is called when the page is loaded
    // so we need to reset the selectedProducts array in case of compare products
    if (
      this.selection === SelectionType.CompareProducts &&
      this.replacementBaseProduct
    ) {
      this.selectedProducts = [this.replacementBaseProduct];
    }
  }

  handleSelectedProducts(selectedProducts: Product[]) {
    this.selectedProducts = selectedProducts;
    if (this.selection === SelectionType.CompareProducts) {
      if (this.replacementBaseProduct?.selected) {
        // use unshift to display the base product first in the compare modal
        this.selectedProducts.unshift(this.replacementBaseProduct);
      }
      this.toggleCardCheckBoxes(this.selectedProducts?.length >= 4);
    }
    this.buildActionBarSelectedProductText();
  }

  toggleCardCheckBoxes(disabled: boolean) {
    this.newSearchViewModel?.products?.forEach(product => {
      if (!this.selectedProducts.includes(product)) {
        product.disabled = disabled;
      }
    });
  }

  redirectToComparePage() {
    this.navigationHelperService.routeTo(PATHS.SEARCH_COMPARE, {
      selectedProducts: this.getProductNumbersAsQueryParam(),
    });
  }

  getProductNumbersAsQueryParam() {
    return this.selectedProducts.map(value => value.productNumber).join(',');
  }

  calculateItemHeights() {
    const platform = this.platformService.getPlatformType();
    if (platform === PlatformEnum.desktop) {
      this.itemHeights = this.newSearchViewModel.products.map(product =>
        calculateDesktopProductHeightForSearch(
          product,
          !!this.powerReviewsMap?.get(product.productNumber.toString()),
          this.isGuestUser,
        ),
      );
    } else if (platform === PlatformEnum.tablet) {
      this.itemHeights = this.newSearchViewModel.products.map(product =>
        calculateTabletProductHeightForSearch(
          product,
          !!this.powerReviewsMap?.get(product.productNumber.toString()),
          this.isGuestUser,
        ),
      );
    } else if (platform === PlatformEnum.mobile) {
      this.itemHeights = this.newSearchViewModel.products.map(product =>
        calculateMobileProductHeightForSearch(
          product,
          !!this.powerReviewsMap?.get(product.productNumber.toString()),
          this.isGuestUser,
        ),
      );
    }
    if (
      this.itemHeights?.length > 0 &&
      this.itemHeights?.length < SEARCH_RECORD_PER_BATCH
    ) {
      this.itemHeights.push(SMALL_RESULT_COUNT_SPACER_HEIGHT);
    }
    this.newSearchViewModel.products = [...this.newSearchViewModel.products];
  }

  get SearchLoadingState(): typeof searchLoadingState {
    return searchLoadingState;
  }

  get SelectionType(): typeof SelectionType {
    return SelectionType;
  }

  getSearchErrorMessage(): string {
    return this.newSearchViewModel.searchText?.length > 0
      ? NEW_SEARCH_ERROR_MESSAGES[0]
      : NEW_SEARCH_ERROR_MESSAGES[1];
  }

  setPowerReviews() {
    this.pwrRatingStarsMap
      .pipe(
        filter(val => Array.from(val.values()).some(v => !!v)),
        take(1),
      )
      .subscribe(ratings => {
        this.powerReviewsMap = ratings;
        if (!!ratings && ratings.size > 0) {
          this.calculateItemHeights();
        }
      });
  }

  getSearchResult(): void {
    this.productsSubscription$?.unsubscribe();

    this.clearSelectedProducts();

    if (!this.newSearchViewModel) {
      this.newSearchViewModel = { products: [] };
    }

    this.newSearchViewModel.loadingState = this.SearchLoadingState.loading;

    if (!!this.facetGenerator?.state) {
      this.commerceEngineFacets = new Map(
        this.facetGenerator.facets.map(facet => [facet.state.facetId, facet]),
      );

      this.newSearchViewModel.facetLoadingState =
        this.SearchLoadingState.loaded;
      this.newSearchViewModel.totalCount =
        this.summary?.state?.totalNumberOfProducts ?? 0;
      this.newSearchViewModel.searchText =
        this.coveoSearch.state['queryExecuted']?.length > 0
          ? this.coveoSearch.state['queryExecuted']
          : null;
    }

    if (
      !!this.coveoSearch?.state?.products &&
      !this.coveoSearch?.state?.error
    ) {
      const coveoResultsProductNumbers = this.coveoSearch?.state?.products.map(
        result => parseInt(result.ec_product_id),
      );
      if (coveoResultsProductNumbers.length > 0) {
        const includeWorksWellWithProducts = !this.isGuestUser;
        this.productService.loadProducts(
          coveoResultsProductNumbers,
          includeWorksWellWithProducts,
        );

        if (!!this.productsSubscription$) {
          this.productsSubscription$.unsubscribe();
        }
        let productNumbers: Array<{ divisionApn: string; attributes: any }> =
          [];
        let isPageLoadTracked = true;
        this.productsSubscription$ = this.productService
          .getProductsWithAlternatives(coveoResultsProductNumbers)
          .pipe(
            filter(
              products =>
                !!products &&
                products.size === coveoResultsProductNumbers.length &&
                products.size > 0 &&
                Array.from(products.values()).every(product => !!product),
            ),
          )
          .subscribe(products => {
            productNumbers = Array.from(products.values()).map(value => ({
              divisionApn:
                value?.summary?.divisionNumber + '-' + value?.productNumber,
              attributes: value?.trackingAttributes,
            }));

            if (isPageLoadTracked && !this.isLoadingNextBatch) {
              this.trackingLoadPage(productNumbers);
              isPageLoadTracked = false;
            }

            if (isPageLoadTracked && this.isLoadingNextBatch) {
              this.trackingLoadMorePage(productNumbers);
              isPageLoadTracked = false;
              this.isLoadingNextBatch = false;
            }

            this.newSearchViewModel = this.buildNewSearchViewModel(products);
            this.calculateItemHeights();
          });
        this.pwrRatingStarsMap =
          this.powerReviewService.loadPowerReviewsProductFromStore();
      } else {
        this.newSearchViewModel = this.buildNewSearchViewModel(new Map());
      }
    }
  }

  trackingLoadPage(productNumbers: any[]) {
    this.newSearchService.trackSearchResultPageLoad(
      '',
      '',
      SEARCH_TYPE.CATALOG_SEARCH,
      productNumbers,
      this.currentSearchTerm,
      this.newSearchViewModel.totalCount,
    );
  }

  trackingLoadMorePage(productNumbers: any[]) {
    // we are going to impl this method in the future
    // Do not delete it, because we are overriding it from search list page
  }

  private buildNewSearchViewModel(products: Map<number, Product>) {
    if (products.size === 0) {
      this.recommendationsController.refresh();
    }
    const result: NewSearchViewModel = {
      products: Array.from(products.values()).map(
        value => ({ ...value }) as SearchResultViewModel,
      ),
      totalCount: this.summary?.state?.totalNumberOfProducts ?? 0,
      facetLoadingState: this.SearchLoadingState.loaded,
      loadingState: this.SearchLoadingState.loaded,
      searchText:
        this.coveoSearch.state['queryExecuted']?.length > 0
          ? this.coveoSearch.state['queryExecuted']
          : null,
    };

    if (this.coveoSearch?.state?.error) {
      result.facetLoadingState = this.SearchLoadingState.error;
      result.loadingState = this.SearchLoadingState.error;
      result.totalCount = 0;
      result.searchText = this.currentSearchTerm;
    }
    return result;
  }

  nextBatch(currentElementIndex: number) {
    const totalElements = this.newSearchViewModel?.products?.length;
    const totalCount = this.newSearchViewModel?.totalCount;
    const { loadingState } = this.newSearchViewModel ?? {};
    const ELEMENTS_BEFORE_PAGES_END = 20;
    if (
      totalElements !== 0 &&
      totalElements < totalCount &&
      currentElementIndex > totalElements - ELEMENTS_BEFORE_PAGES_END &&
      !this.isLoadingNextBatch &&
      loadingState !== searchLoadingState.loadingNextPage &&
      loadingState !== searchLoadingState.loading
    ) {
      this.isLoadingNextBatch = true;
      this.newSearchViewModel.loadingState = searchLoadingState.loadingNextPage;
      this.paginationController.fetchMoreProducts();
    }
  }

  openActionModal() {
    this.openAddToListModal();
  }

  openAddToListModal() {
    this.addToListModalService.openModal(this.selectedProducts, false, false);
    this.clearSearchSub$ = this.addToListModalService.clearSearch.subscribe(
      () => {
        this.clearSelectedProducts();
      },
    );
  }

  closeAndDeatachAddToListModal = () => {
    this.addToListModalService.closeModal();
    this.clearSearchSub$?.unsubscribe();
  };

  async openAddToInventoryModal() {
    const selectedProductNumbers = this.selectedProducts.map(
      prod => prod.productNumber,
    );
    let inventoryId = this.inventoryService.exposedLastInventory;
    this.inventoryService
      .selectInventoryById$(inventoryId)
      .pipe(take(1))
      .subscribe(async inventory => {
        const ret = this.inventoryService.openAddToInventoryModal(
          selectedProductNumbers,
          this.platformService.isTouch.value,
          this.platformService.platformType,
          inventory,
          false,
        );
        (await ret).onDidDismiss().then(res => {
          if (res.data?.didSubmit) {
            this.clearSelectedProducts();
          }
        });
      });
  }

  checkIsLastPage() {
    this.isLastPage =
      this.newSearchViewModel.products.length === 0 ||
      this.newSearchViewModel.products.length ===
        this.newSearchViewModel?.totalCount;
  }

  setToggleIsDisabled(newSearchViewModel: NewSearchViewModel) {
    if (this.isUsFoodsDeliveredFilterPresent(newSearchViewModel)) {
      this.searchDeliveryToggleService.toggleIsDisabled = false;
    } else {
      this.searchDeliveryToggleService.toggleIsDisabled = true;
    }
  }

  isUsFoodsDeliveredFilterPresent(
    newSearchViewModel: NewSearchViewModel,
  ): boolean {
    let isDeliveryMethodFilter = false;
    newSearchViewModel?.facets?.forEach(facet => {
      if (facet.label === 'delivery_method') {
        isDeliveryMethodFilter = facet.values.some(
          v => v.label === DELIVERY_METHOD_US_FOODS,
        );
      }
    });
    return isDeliveryMethodFilter;
  }

  setToggleIsOn(newSearchViewModel: NewSearchViewModel) {
    const facet = newSearchViewModel?.facets
      ?.filter(
        value => value?.fieldName === DELIVERY_METHOD_FACET_FIELD_NAME,
      )[0]
      ?.values?.filter(value => value?.label === DELIVERY_METHOD_US_FOODS)[0];
    if (facet?.isApplied) {
      this.searchDeliveryToggleService.toggleIsOn = true;
    } else {
      this.searchDeliveryToggleService.toggleIsOn = false;
    }
  }

  getFacetFilterFromResponse(newSearchViewModel: NewSearchViewModel): string {
    let facetFilters = '';
    Object.entries(newSearchViewModel?.facetFilters)?.forEach(
      ([key, value]) => {
        value?.forEach(
          f => (facetFilters = facetFilters.concat(`${key}:${f},`)),
        );
      },
    );
    facetFilters = facetFilters.slice(0, -1);
    return facetFilters;
  }

  setBaseProduct(productNumber: number): void {
    this.newSearchService
      .getReplacementBaseProduct(productNumber)
      .pipe(
        filter(baseProduct => !!baseProduct),
        take(1),
      )
      .subscribe(baseProduct => {
        this.replacementBaseProduct = {
          ...baseProduct,
          alternative: {
            ...baseProduct.alternative,
            available: false,
          },
        };
        this.updateBaseProduct(this.selection);
      });
  }

  observeToggleState() {
    this.toggleStateSub$ = this.searchDeliveryToggleService.toggleState
      .pipe(delay(1000))
      .subscribe(toggleIsOn => {
        let selectedFacetFilters = [
          ...this.newSearchViewModel.selectedFacetFilters,
        ];
        if (toggleIsOn) {
          selectedFacetFilters.push(
            buildFacetFilterObject(
              DELIVERY_METHOD_FACET_FIELD_NAME,
              DELIVERY_METHOD_US_FOODS,
            ),
          );
        } else {
          selectedFacetFilters =
            this.newSearchViewModel.selectedFacetFilters.filter(
              v => v.facetValue.value !== DELIVERY_METHOD_US_FOODS,
            );
        }
      });
  }

  onFacetsSelected($event) {
    this.toggleFacets($event);
  }

  onFacetsListSelected($event) {
    let countValuesSelected = 0;
    if (this.listFacetSharedObject?.isValuesSelected) {
      this.addListFacetParam();
      const customerNumber =
        this.selectedCustomerState?.customerNumber?.toString();
      this.listFacetSharedObject.values?.forEach((value, key) => {
        if (value?.isSelected) {
          this.coveoService.setCustomContext(value.id, customerNumber);
          countValuesSelected++;
        }
      });
      this.listFacetSharedObject.countValuesSelected = countValuesSelected;
    } else {
      this.deleteListFacetParam();
      this.listFacetSharedObject.values?.forEach((value, key) => {
        if (!value?.isSelected) {
          this.coveoService.deleteCustomContextKey(value.id);
        }
      });
      this.listFacetSharedObject.countValuesSelected = 0;
    }
    this.coveoCommerceEngine.dispatch(this.searchActions.executeSearch());
    if (this.pageType === 'productListing') {
      const listingController = this.coveoSearch as ProductListing;
      listingController.refresh();
    }
  }

  buildListFacet() {
    if (this.isGuestUser) {
      return;
    }
    this.listFacetSharedObject = new ListFacetSharedObject();
    this.listFacetSharedObject.displayName = this.translateService.instant(
      'i18n.newSearch.lists',
    );
    this.listFacetSharedObject.isValuesSelected = false;
    this.listFacetSharedObject.countValuesSelected = 0;
    this.listFacetSharedObject.values = new Map<string, ListFacetValue>([
      [
        ListFacetFilterKey.MY_LIST,
        {
          value: this.translateService.instant('i18n.newSearch.myLists'),
          id: ListFacetFilterKey.MY_LIST,
          isSelected: false,
        } as ListFacetValue,
      ],
      [
        ListFacetFilterKey.RECENT_PURCHASE,
        {
          value: this.translateService.instant(
            'i18n.newSearch.recentlyPurchased',
          ),
          id: ListFacetFilterKey.RECENT_PURCHASE,
          isSelected: false,
        } as ListFacetValue,
      ],
    ]);
  }

  isListFacetWithParamExist() {
    let params: Params = this.navigationHelperService.getParamAsObject();
    return (
      params?.listFacetValues !== undefined && params?.listFacetValues !== ''
    );
  }

  setUpListFacetWithParam(params: Params) {
    let countValuesSelected = 0;
    if (this.isListFacetWithParamExist()) {
      params?.listFacetValues.split(',').forEach(value => {
        this.coveoService.setCustomContext(
          value,
          this.selectedCustomerState?.customerNumber?.toString(),
        );
        this.listFacetSharedObject.values.get(value).isSelected = true;
        this.listFacetSharedObject.isValuesSelected = true;
        countValuesSelected++;
      });
      this.listFacetSharedObject.countValuesSelected =
        countValuesSelected > 0 ? countValuesSelected : 0;
    }
  }

  isNewSearchExecuted(searchQueryParam: string): boolean {
    if (this.searchQueryParamStack.length === 0) {
      this.searchQueryParamStack.push(searchQueryParam);
    }
    if (this.searchQueryParamStack[0] !== searchQueryParam) {
      this.searchQueryParamStack.pop();
      return true;
    }
    return false;
  }

  clearListFacetFilter() {
    this.coveoService.clearCustomContext();

    if (!!this.listFacetSharedObject) {
      this.listFacetSharedObject.isValuesSelected = false;
      this.listFacetSharedObject.values.forEach((value, key) => {
        value.isSelected = false;
      });
      this.listFacetSharedObject.countValuesSelected = 0;
    }

    this.deleteListFacetParam();
  }

  addListFacetParam() {
    const urlTree = this.router.parseUrl(this.location.path());
    urlTree.queryParams['listFacetValues'] = Array.from(
      this.listFacetSharedObject.values.keys(),
    ).join(',');
    this.location.replaceState(urlTree.toString());
  }

  deleteListFacetParam() {
    const urlTree = this.router.parseUrl(this.location.path());
    delete urlTree.queryParams['listFacetValues'];
    this.location.replaceState(urlTree.toString());
  }

  toggleFacets($event: ToggleFacetEvent) {
    switch ($event.facet.type) {
      case 'hierarchical':
        if ($event.facet.isValueSelected($event.value as CategoryFacetValue)) {
          $event.facet.deselectAll();
          break;
        } else {
          $event.facet.toggleSelect($event.value as CategoryFacetValue);
          break;
        }
      case 'regular':
        $event.facet.toggleSelect($event.value as RegularFacetValue);
    }
  }

  clearAllFacets() {
    this.clearListFacetFilter();
    this.facetGenerator.deselectAll();
  }

  scrollToTop(noresults?: boolean) {
    if (noresults) {
      this.ionContent?.scrollToTop();
    } else {
      this.newSearchResultComponent?.scrollToIndex(0);
    }
  }

  facetLoadValues($event: FacetViewEvent) {
    this.isLoadingFacetValues = true;
    $event.operation === this.facetViewEnum.viewMore
      ? $event.facet.showMoreValues()
      : $event.facet.showLessValues();
  }

  buildActionBarSelectedProductText() {
    let text = `${this.selectedProducts?.length} `;
    const selectedText = 'Selected';
    if (
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.mobile
    ) {
      text = text.concat(selectedText);
    } else {
      const productText =
        this.selectedProducts?.length > 1 ? 'Products ' : 'Product ';
      text = text.concat(productText).concat(selectedText);
    }
    this.selectedProductText = text;
  }

  public setGoBackText() {
    const text = {
      [SEARCH_PAGES_ENUM.list]: 'i18n.lists.myLists',
      [SEARCH_PAGES_ENUM.listDetail]: 'i18n.lists.listDetails',
      [SEARCH_PAGES_ENUM.inventoryWorksheet]:
        'i18n.inventory.inventoryWorksheetTitle',
    };
    this.backButtonText = text[this.originSearchPage] || '';
  }

  setUpParams(params: Params) {
    this.selection = params.selectionType ?? '';
    this.isAddToList = params?.addToList;
    this.isInventory = params?.isInventory;
    this.isAddIngredient = params?.isAddIngredient;
    this.originSearchPage = params?.originSearchPage;
    this.currentSearchTerm = params['searchText'] ?? '';
  }

  handleInPageSearch($event: string) {
    if ($event?.trim().length > 0) {
      this.coveoService.deleteCustomContextKey(ListFacetFilterKey.MY_LIST);
      this.deleteListFacetParam();
      this.searchBoxController.updateText($event);
      this.searchBoxController.submit();
    }
  }
  onSearchIconClick() {
    this.isInPageSearchModalOpen = true;
  }
  handleInPageSearchModalEvent($event) {
    if ($event?.searchText) {
      this.handleInPageSearch($event.searchText);
    }
    this.isInPageSearchModalOpen = false;
  }

  fetchTopSellers$(topSellersProductNumbers: number[]) {
    return this.newSearchService.getTopSellersViewModels$(
      topSellersProductNumbers,
    );
  }

  async addIngredient() {
    await this.recipeDetailService.addProductsToRecipeFromSearch(
      this.selectedProducts,
    );
    this.newSearchService.trackAddIngredient(this.selectedProducts);
    this.navigationHelperService.redirectionBack();
  }

  trackBackButton() {
    if (this.isAddIngredient) {
      this.newSearchService.trackAddIngredientBackButton(true);
    }
  }

  expandTopSectionText() {
    this.isTopSectionTextExpanded = !this.isTopSectionTextExpanded;
  }
}
